import VoiceAPI from 'dashboard/api/channels/voice';
import {
  TERMINAL_STATUSES,
  NON_OFFERABLE_STATUSES,
  currentAccountIdFromUrl,
} from 'dashboard/helper/voice';
import { fromActiveCallRow } from 'dashboard/helper/voiceRealtime';
import { useConfig } from 'dashboard/composables/useConfig';
import SessionStorage from 'shared/helpers/sessionStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';

const INCOMING_CALL_STORAGE_TTL_MS = 5 * 60 * 1000;
// Active calls live longer than a ring, so the recovery blob gets a wider window:
// long enough that a refresh mid-call still finds it, short enough that a blob for
// a long-ended call expires before it can seed a stale Reconnect.
const ACTIVE_CALL_STORAGE_TTL_MS = 10 * 60 * 1000;

// Precedence for the SINGLE recoverableCall slot when a boot hydrate returns more than one
// candidate row (lowest rank wins). See the selection block in hydrateActiveCalls.
const RECOVERABLE_RANK = Object.freeze({
  IN_PROGRESS: 0,
  OUTBOUND_RINGING: 1,
});

// Race guard for the boot hydrate. The server snapshot is asynchronous, so a
// realtime event (a fresh ring, an answer, a hang-up) can land between the
// fetch starting and resolving. We stamp every call-state mutation with the
// time it happened, per callSid, and the hydrate ignores any row whose callSid
// was mutated after the fetch began. `terminalCallSids` additionally remembers
// every callSid that reached a terminal status this session so a stale server
// row can never resurrect a call that has already ended.
const callMutationTimestamps = new Map();
const terminalCallSids = new Set();

// A callSid seen at 'in-progress' this session: somebody answered it. Mirrors
// terminalCallSids and blocks the same resurrection one status earlier. Needed because
// MESSAGE_CREATED / MESSAGE_UPDATED are not re-fetched by ActionCableBroadcastJob, so a
// Sidekiq backlog can deliver 'in-progress' BEFORE the 'ringing' frame that queues the
// offer - which would otherwise ring a call another agent is already on.
const answeredCallSids = new Set();

const stampCallMutation = callSid => {
  if (callSid) callMutationTimestamps.set(callSid, Date.now());
};

// Test-only: reset the module-level race-guard tracking between cases.
const resetCallMutationTracking = () => {
  callMutationTimestamps.clear();
  terminalCallSids.clear();
  answeredCallSids.clear();
};

// Incoming calls are a QUEUE: a second (or third) inbound ring can arrive while
// an earlier one is still pending. Persistence stores an ARRAY of those pending
// offers so a refresh restores every pending ring at once; the server hydrate then
// reconciles. All helpers below operate on that array. The queue is inbound-only
// (see the outboundCall state slot), so nothing here filters by direction.

// Normalizes whatever is stored under the INCOMING_CALL key into an array. Older
// single-object blobs (pre-queue) are wrapped so a refresh mid-migration still
// recovers the one pending ring.
const readRawPersistedIncomingCalls = () => {
  // TTL expiry + corrupt-entry cleanup are handled by SessionStorage.getWithExpiry.
  const stored = SessionStorage.getWithExpiry(
    SESSION_STORAGE_KEYS.INCOMING_CALL
  );
  if (!stored) return [];
  if (Array.isArray(stored)) return stored;
  return [stored];
};

const writePersistedIncomingCalls = calls => {
  if (!calls.length) {
    SessionStorage.remove(SESSION_STORAGE_KEYS.INCOMING_CALL);
    return;
  }
  SessionStorage.setWithExpiry(
    SESSION_STORAGE_KEYS.INCOMING_CALL,
    calls,
    INCOMING_CALL_STORAGE_TTL_MS
  );
};

// Removes one persisted offer by callSid. There is deliberately no clear-all branch here:
// wiping the blob wholesale would drop a co-pending second ring, so every caller names the
// offer it is dismissing. The one place that really does clear everything (resetVoiceSession)
// calls writePersistedIncomingCalls([]) explicitly.
const clearPersistedIncomingCall = callSid => {
  const remaining = readRawPersistedIncomingCalls().filter(
    call => call?.callSid !== callSid
  );
  writePersistedIncomingCalls(remaining);
};

// Returns the pending, non-terminal, account-matching offers to seed state on
// module load. Terminal/foreign-account entries are pruned; the pruned array is
// written back so a stale entry can never seed a ring.
const readPersistedIncomingCalls = () => {
  const raw = readRawPersistedIncomingCalls();
  if (!raw.length) return [];

  const currentAccountId = currentAccountIdFromUrl();
  const fresh = raw.filter(callData => {
    const isTerminal = TERMINAL_STATUSES.includes(callData?.status);
    const accountMismatch =
      currentAccountId != null &&
      callData?.accountId != null &&
      Number(callData.accountId) !== currentAccountId;
    return callData?.callSid && !isTerminal && !accountMismatch;
  });

  // Persist the pruned set back (drops terminal/foreign entries from storage).
  if (fresh.length !== raw.length) {
    writePersistedIncomingCalls(fresh);
  }

  return fresh;
};

const clearPersistedActiveCall = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.ACTIVE_CALL);
};

// Reads the persisted active call for the module-init seed. Mirrors
// readPersistedIncomingCalls: TTL + corrupt-entry cleanup come free from
// getWithExpiry; here we additionally drop terminal blobs (a call that ended)
// and account mismatches (a blob persisted under a different account).
const readPersistedActiveCall = () => {
  const callData = SessionStorage.getWithExpiry(
    SESSION_STORAGE_KEYS.ACTIVE_CALL
  );
  if (!callData) return null;

  const isTerminal = TERMINAL_STATUSES.includes(callData?.status);
  const currentAccountId = currentAccountIdFromUrl();
  const accountMismatch =
    currentAccountId != null &&
    callData?.accountId != null &&
    Number(callData.accountId) !== currentAccountId;

  if (!callData?.callSid || isTerminal || accountMismatch) {
    SessionStorage.remove(SESSION_STORAGE_KEYS.ACTIVE_CALL);
    return null;
  }

  return callData;
};

const persistActiveCall = activeCall => {
  // serverConfirmed must NOT persist: this blob seeds recoverableCall synchronously on the NEXT
  // refresh, and that seed must stay manual-only (auto-reconnect requires a fresh server
  // confirmation from the hydrate, not a stale persisted flag that would auto-rejoin a dead call).
  const persistable = {
    ...activeCall,
    startedAt: activeCall.startedAt,
    conference_sid: activeCall.conference_sid,
  };
  delete persistable.serverConfirmed;
  // consult is server-authoritative too: this blob re-seeds recoverableCall under a TTL on the next
  // refresh, and a stale persisted consult must never drive the UI - only a fresh server row may.
  delete persistable.consult;
  SessionStorage.setWithExpiry(
    SESSION_STORAGE_KEYS.ACTIVE_CALL,
    persistable,
    ACTIVE_CALL_STORAGE_TTL_MS
  );
};

const state = {
  activeCall: null,
  // Queue of pending INBOUND offers (FIFO). A second concurrent ring is appended, not
  // dropped, so a free agent can still pick up the second caller. Seeded synchronously
  // from the persisted array so a refresh restores every pending ring instantly, before
  // the server hydrate reconciles. Inbound-only by construction: setIncomingCall is the
  // single writer and routes the agent's own outbound call to outboundCall below.
  incomingCalls: readPersistedIncomingCalls(),
  // The agent's OWN in-flight outbound call, from the moment its ring is reported until it
  // goes active or ends. It gets a dedicated slot rather than sharing the incoming queue
  // because the two states are read differently: the queue's FIRST entry named the widget's
  // header while the inbound-filtered list decided whether the widget was "ringing", so a
  // mixed [outbound, inbound] queue rendered the outbound callee under an "Incoming call"
  // heading and left the agent with no End button for their own in-flight call.
  // Memory-only on purpose: an outbound call must never be recovered from sessionStorage
  // (the widget's auto-join watcher would dial a conference that is long dead). After a
  // refresh the boot hydrate re-offers it as a manual Reconnect instead.
  outboundCall: null,
  // A call this agent was in (in-progress, joined_by them) that survived a page
  // refresh: the boot hydrate offers it as a "Reconnect" so the agent can
  // rejoin the conference. Carries conference_sid + call_started_at for rejoin.
  // Seeded synchronously at module load from the persisted active-call blob so a
  // refresh shows Reconnect instantly, before the server hydrate returns. The
  // WebRTC leg is gone after a refresh, so it becomes a Reconnect offer (not
  // activeCall). The hydrate is authoritative: if the server no longer reports
  // the call, reconciliation clears this seed.
  recoverableCall: readPersistedActiveCall(),
  // Surfaced media/join failures (mic permission, dead audio, join failure).
  // Codes are plain strings: MIC_BLOCKED, MIC_NOT_FOUND, JOIN_FAILED,
  // NO_AUDIO_OUTPUT.
  callError: null,
  // The context of an outbound join that FAILED (caller is on hold server-side). Lets the
  // widget offer a manual Retry that re-joins the SAME conference without re-initiating.
  retryableOutbound: null,
  // Pending TRANSIENT mid-call media warnings (CALL_WARNING_CODES), drained by App.vue into
  // self-dismissing toasts. Deliberately NOT part of callError: a warning resolves on its own
  // and must never take over the error banner, which owns actionable failures and their
  // Retry/Dismiss actions.
  //
  // A QUEUE, not a single slot. StatsMonitor#_raiseWarnings evaluates every threshold in one
  // synchronous pass, so bytesSent and bytesReceived breaching together raise two warnings
  // before Vue flushes the watcher - precisely the "no audio in either direction" case. A single
  // slot would let the second overwrite the first and report only one direction to the agent.
  callWarnings: [],
};

const getters = {
  getActiveCall: $state => $state.activeCall,
  hasActiveCall: $state => !!$state.activeCall,
  // Full pending-offer queue. Inbound-only, so no consumer has to filter by direction.
  getIncomingCalls: $state => $state.incomingCalls,
  // Scalar getters for consumers that only care about the PRIMARY offer: the first/oldest
  // still-pending ring (FIFO), and "has incoming" is length>0.
  getIncomingCall: $state => $state.incomingCalls[0] || null,
  hasIncomingCall: $state => $state.incomingCalls.length > 0,
  // A FREE agent (no active call) with at least one pending ring: the single signal that
  // drives the global ringtone. A busy agent's queued ring stays silent until they free up.
  // The agent's own outbound call lives in its own slot and never reaches this queue, so
  // there is nothing to filter out here. App.vue owns the ringtone; the widget does not.
  hasRingableIncomingCall: $state =>
    $state.incomingCalls.length > 0 && !$state.activeCall,
  getOutboundCall: $state => $state.outboundCall,
  hasOutboundCall: $state => !!$state.outboundCall,
  getRecoverableCall: $state => $state.recoverableCall,
  hasRecoverableCall: $state => !!$state.recoverableCall,
  getCallError: $state => $state.callError,
  hasCallError: $state => !!$state.callError,
  getRetryableOutbound: $state => $state.retryableOutbound,
  hasRetryableOutbound: $state => !!$state.retryableOutbound,
  getCallWarnings: $state => $state.callWarnings,
};

const actions = {
  // A consult push updates only the consult sub-state of the ACTIVE call (an agent holds one). It
  // arrives with no status (voiceRealtime.handleVoiceMessageUpdated), so it is applied here rather
  // than through handleCallStatusChanged. A push for any other call is ignored; null clears it.
  handleConsultUpdated({ state: currentState, commit }, { callSid, consult }) {
    if (!callSid || callSid !== currentState.activeCall?.callSid) return;
    commit('SET_ACTIVE_CALL_CONSULT', consult);
  },

  handleCallStatusChanged(
    { state: currentState, dispatch },
    { callSid, status }
  ) {
    const isActiveCall = callSid === currentState.activeCall?.callSid;
    const queuedCall = currentState.incomingCalls.find(
      call => call.callSid === callSid
    );
    const isIncomingCall = !!queuedCall;
    const terminalStatuses = [...TERMINAL_STATUSES];

    if (terminalStatuses.includes(status)) {
      // Remember it ended so a stale boot-hydrate row can never re-offer it.
      if (callSid) terminalCallSids.add(callSid);
      // A terminal status for the call whose outbound-join failed clears the stale Retry context:
      // the conference is dead, so a Retry would re-join a conference that no longer exists.
      // retryableOutbound is its own state bit (independent of the active/incoming branches
      // below), so reconcile it here regardless of which branch this terminal event hits.
      if (callSid && currentState.retryableOutbound?.callSid === callSid) {
        dispatch('clearRetryableOutbound');
      }
      // Same for the agent's own outbound call (busy / no-answer / the far end hung up
      // before the join landed): outboundCall is its own state bit, independent of the
      // active/incoming branches below, so reconcile it here whichever branch this
      // terminal event hits. Left behind, it would keep the widget's Outgoing card - and
      // its End button - on screen for a call that is already over.
      if (callSid && currentState.outboundCall?.callSid === callSid) {
        dispatch('clearOutboundCall');
      }
      if (isActiveCall) {
        dispatch('clearActiveCall');
      } else if (isIncomingCall) {
        // Clear just this call from the queue (+ its persisted copy); any other
        // pending ring stays.
        dispatch('clearIncomingCall', callSid);
      } else {
        clearPersistedIncomingCall(callSid);
        // A terminal event for the persisted (seeded) active call: drop the blob
        // AND the in-memory Reconnect offer so neither a refresh nor the current
        // session keeps a Reconnect for a call that already ended.
        if (callSid && currentState.recoverableCall?.callSid === callSid) {
          clearPersistedActiveCall();
          dispatch('clearRecoverableCall');
        }
      }
      return;
    }

    // The matching queued call just went in-progress, i.e. another tab or agent
    // answered it: drop only that now-stale offer (clearIncomingCall also clears
    // its persisted entry). Keyed to the matching callSid, so a different second
    // call ringing in is left untouched and still answerable. The queue is
    // inbound-only, so this can no longer reach the agent's own outbound call:
    // that one lives in outboundCall and must SURVIVE 'in-progress' (its join is
    // still in flight), to be cleared by setActiveCall once the join lands or by
    // the terminal branch above.
    if (status === 'in-progress' && !isActiveCall) {
      // Recorded even with nothing queued: a reordered ActionCable delivery can land
      // 'in-progress' BEFORE the ringing frame that queues the offer, and without this the
      // late ringing frame phantom-rings a call somebody else already answered.
      if (callSid) answeredCallSids.add(callSid);
      if (isIncomingCall) dispatch('clearIncomingCall', callSid);
    }
  },

  setActiveCall({ commit, dispatch, state: currentState }, callData) {
    if (!callData?.callSid) return;

    if (callData.status && TERMINAL_STATUSES.includes(callData.status)) {
      terminalCallSids.add(callData.callSid);
      // A terminal status always drops the persisted active blob so a refresh
      // can't seed a Reconnect for a call that already ended (clearActiveCall
      // also clears it, but the in-memory call may not match the persisted one).
      clearPersistedActiveCall();
      dispatch('clearRetryableOutbound');
      if (currentState.outboundCall?.callSid === callData.callSid) {
        dispatch('clearOutboundCall');
      }
      if (callData.callSid === currentState.activeCall?.callSid) {
        dispatch('clearActiveCall');
      }
      return;
    }

    // Persist the active call so a refresh can offer a Reconnect immediately,
    // before the server hydrate confirms it.
    persistActiveCall(callData);
    commit('SET_ACTIVE_CALL', callData);
    // Once a call is active there is nothing left to "recover": drop any Reconnect
    // offer so a stale phantom Reconnect cannot linger after auto-reconnect + hangup.
    dispatch('clearRecoverableCall');
    dispatch('clearRetryableOutbound');
    // The dial is over too: a lingering outboundCall would keep the widget on its
    // "Outgoing call" card and shadow the in-progress card, so the agent would never
    // see the live duration. A device carries one call at a time, so whichever call
    // just went active ends any outbound dial.
    dispatch('clearOutboundCall');
  },

  clearActiveCall({ commit }) {
    // End the WebRTC connection before clearing the call state
    try {
      VoiceAPI.endClientCall();
    } catch (error) {
      // Error ending client call during clearActiveCall
    }

    clearPersistedActiveCall();
    commit('CLEAR_ACTIVE_CALL');
  },

  // The single entry point for "the server reports a call offer for this agent". Inbound
  // rings are QUEUED; the agent's OWN outbound call is routed to its dedicated slot so the
  // queue stays inbound-only. The routing lives here rather than at the call sites (the
  // realtime handler is the only producer today) so the invariant is enforced at the one
  // place that writes the queue and no future producer can reintroduce a mixed queue.
  setIncomingCall({ commit, dispatch, state: currentState }, callData) {
    if (!callData?.callSid) return;
    if (currentState.activeCall?.callSid === callData.callSid) return;

    // A terminal-status offer must never be queued (e.g. a late event for a call
    // that already ended). Same short-circuit the persistence hydrate applies.
    if (callData.status && TERMINAL_STATUSES.includes(callData.status)) return;

    // A call that already reached a terminal status THIS session must not be resurrected by a
    // delayed/reordered ringing frame that arrives after the terminal one. terminalCallSids is
    // the same guard the boot hydrate uses; here it also gates realtime insertion.
    if (terminalCallSids.has(callData.callSid)) return;

    // The agent's own outbound call: its own slot, never the queue and never persisted.
    if (callData.isOutbound) {
      dispatch('setOutboundCall', callData);
      return;
    }

    // Somebody already answered this call this session, so a 'ringing' frame arriving now can
    // only be a reordered delivery: never re-offer it. Checked AFTER the outbound routing
    // above, because the agent's OWN outbound call legitimately reports 'in-progress' while
    // its join is still in flight and its slot must survive that.
    if (answeredCallSids.has(callData.callSid)) return;

    // ADD_INCOMING_CALL is the SINGLE dedup authority: a same-sid re-offer updates
    // the existing queue entry in place; a distinct sid appends (a second concurrent
    // ring is always queued, never dropped, regardless of arrival order). Commit
    // first, then mirror the resulting queue into storage -
    // currentState.incomingCalls reflects the mutation synchronously - so the dedup
    // is computed exactly once.
    commit('ADD_INCOMING_CALL', callData);
    writePersistedIncomingCalls(currentState.incomingCalls);
  },

  setOutboundCall({ commit }, callData) {
    if (!callData?.callSid) return;
    commit('SET_OUTBOUND_CALL', callData);
  },

  clearOutboundCall({ commit }) {
    commit('CLEAR_OUTBOUND_CALL');
  },

  // Dismiss the offer for ONE callSid: drop it from the inbound queue (and its persisted
  // copy) and clear the outbound slot when it is that call. Callers - the Call 'accept'
  // handler, endCall, reject, the hydrate reconciliation - know the sid, not which slot
  // holds it, so the routing lives here alongside setIncomingCall's.
  // The sid is REQUIRED: the old no-arg branch wiped the entire queue, which would silently
  // drop a co-pending second ring and make that caller unanswerable. The one place that
  // genuinely clears everything is resetVoiceSession.
  clearIncomingCall({ commit, state: currentState }, callSid) {
    if (!callSid) return;
    clearPersistedIncomingCall(callSid);
    commit('REMOVE_INCOMING_CALL', callSid);
    if (currentState?.outboundCall?.callSid === callSid) {
      commit('CLEAR_OUTBOUND_CALL');
    }
  },

  setRecoverableCall({ commit }, callData) {
    commit('SET_RECOVERABLE_CALL', callData);
  },

  clearRecoverableCall({ commit }) {
    commit('CLEAR_RECOVERABLE_CALL');
  },

  setRetryableOutbound({ commit }, context) {
    commit('SET_RETRYABLE_OUTBOUND', context);
  },
  clearRetryableOutbound({ commit }) {
    commit('CLEAR_RETRYABLE_OUTBOUND');
  },

  // Resolve THIS agent's voice session and wipe every trace of it. Dispatched when the
  // dashboard switches accounts: none of the state above is account-scoped, so without
  // this an account-A call stays on screen in account B, and its End button would send
  // the leave through VoiceAPI's CURRENT-route URL, i.e. account B, carrying account A's
  // conversation/inbox ids.
  //
  // Deliberately LOCAL-only, no server call: by the time an account-change watcher runs
  // the route already points at the new account, so a leave would be addressed to the
  // wrong account - where those ids may even resolve to a DIFFERENT conversation. The
  // customer's leg is released server-side by the conference watchdog instead.
  resetVoiceSession({ commit }) {
    try {
      // Bumps teardownGeneration first, so a join still dialing for the old account
      // abandons instead of re-adopting a live leg after the switch.
      VoiceAPI.endClientCall();
      // The device holds a Twilio token minted for the OLD account's inbox, so it must
      // not be reused (or left registered) after the switch.
      VoiceAPI.destroyDevice();
    } catch (error) {
      // A half-initialized device must never block the account switch.
    }

    clearPersistedActiveCall();
    writePersistedIncomingCalls([]);
    commit('RESET_CALL_STATE');
  },

  // Boot recovery: ask the server which calls this agent can still act on and
  // rebuild the offer state a page refresh wiped. Inbox-wide ringing calls are
  // re-offered (setIncomingCall); the agent's own in-progress calls become
  // Reconnect offers (setRecoverableCall). Then reconcile: any locally-held
  // incoming offer the server no longer reports (answered by another tab/agent,
  // or already dead) is cleared. The whole thing is best-effort - a failed
  // fetch leaves state exactly as it was so we never wipe a live offer.
  async hydrateActiveCalls({ dispatch, state: currentState, rootGetters }) {
    // The active_calls route is declared inside `if ChatwootApp.enterprise?`
    // (config/routes.rb), and IS_ENTERPRISE is that exact predicate
    // (dashboard_controller.rb), server-rendered into the layout so it is readable before
    // boot. On a community build the route does not exist, so requesting it would 404 on
    // every account initialization and bury the real signal the logging below exists for.
    // Guarded here rather than at the App.vue call site so the endpoint is protected
    // wherever it is dispatched from.
    if (!useConfig().isEnterprise) return;

    const hydrateStartedAt = Date.now();
    // Capture the account this hydrate is for: a multi-account switch while the fetch is in
    // flight must not let the previous account's rows populate the new account's call state.
    const hydrateAccountId = currentAccountIdFromUrl();

    let rows;
    try {
      rows = await VoiceAPI.fetchActiveCalls();
    } catch (error) {
      // Leave state unchanged; the realtime stream and persisted entry still cover us.
      // Do NOT hide it though - a broken hydrate endpoint is otherwise invisible (the
      // App.vue .catch never fires, because this catch resolves the promise).
      // Console-only, and NOT an oversight to "fix" by adding Sentry: this deployment observes
      // errors through Google Error Reporting, which ingests SERVER logs (Rails writes to
      // stdout -> Cloud Logging), and the browser has no path into that. Frontend Sentry is
      // gated on SENTRY_FRONTEND_DSN / SENTRY_DSN (entrypoints/dashboard.js) which is unset
      // here, so a browser-side capture would be a silent no-op that merely LOOKS like
      // telemetry. The status is included so a misrouted endpoint (404) is distinguishable
      // from a broken finder (5xx) or a dropped connection (no response at all).
      // eslint-disable-next-line no-console
      console.error(
        'voice hydrate failed',
        error?.response?.status ?? 'no response',
        error
      );
      return;
    }
    // Drop a stale response that resolved after the account changed under it.
    if (currentAccountIdFromUrl() !== hydrateAccountId) return;
    if (!Array.isArray(rows)) rows = [];

    // A row may be skipped if a realtime event for the same call landed while
    // the fetch was in flight (a fresher local truth wins) or if the call has
    // already reached a terminal status this session (never resurrect it).
    const isSupersededByLocalState = callSid => {
      if (terminalCallSids.has(callSid)) return true;
      const mutatedAt = callMutationTimestamps.get(callSid);
      return mutatedAt != null && mutatedAt > hydrateStartedAt;
    };

    const inboxGetter = rootGetters['inboxes/getInbox'];
    const presentCallSids = new Set();

    // Proof that THIS browser tab is the one that held a call: sessionStorage is per-tab and
    // survives a refresh, while a freshly opened second tab has none. Read once, up front, so
    // a dispatch below cannot clear it from under the comparison. See the auto-rejoin gate in
    // the in-progress branch.
    const persistedActiveCallSid = readPersistedActiveCall()?.callSid || null;

    // There is ONE recoverableCall slot but TWO row types compete for it (an in-progress call
    // this agent was on, and their own outbound ring still on hold), so a hydrate that returns
    // both must CHOOSE deterministically. Dispatching per row let whichever row the server
    // happened to list last silently overwrite the other offer.
    //
    // Precedence - in-progress outranks an outbound ring:
    //   * the customer on an in-progress call is mid-conversation and only the agent's leg
    //     dropped, so rejoining resumes a live conversation; an outbound ring may not even be
    //     answered yet (nobody is waiting to talk),
    //   * only the in-progress row carries call_started_at, i.e. it is the one the widget can
    //     show a true duration for (and, when the auto-rejoin gate below allows it, the only
    //     one it may auto-reconnect).
    // Ties within the same rank keep the FIRST row: the server lists rows freshest-first
    // (Voice::ActiveCallsFinder orders by last_activity_at desc), so that is the newest call.
    let recoverableOffer = null;
    let recoverableRank = Infinity;
    const considerRecoverable = (offer, rank) => {
      if (rank >= recoverableRank) return;
      recoverableRank = rank;
      recoverableOffer = offer;
    };

    rows.forEach(row => {
      const callSid = row?.call_sid;
      if (!callSid) return;
      presentCallSids.add(callSid);

      const status = row.call_status;
      if (
        NON_OFFERABLE_STATUSES.includes(status) ||
        isSupersededByLocalState(callSid)
      ) {
        return;
      }

      const inboxName = inboxGetter?.(row.inbox_id)?.name;
      const offer = fromActiveCallRow(row, { inboxName });

      if (status === 'ringing') {
        if (offer.isOutbound) {
          // The backend ActiveCallsFinder only returns an outbound ring to its INITIATOR, so this
          // is the agent's own in-flight outbound call. A refresh dropped the memory-only
          // retryableOutbound AND the memory-only outboundCall slot, and the WebRTC leg with
          // them, so this is NOT a dial still in flight: it is a conference the agent has to
          // opt back into. Hence a manual Reconnect rather than the outboundCall slot (which
          // the widget auto-joins): the customer is on hold in the conference and rejoin
          // re-adds the agent leg. No serverConfirmed flag - manual-only, so
          // it never auto-dials into a call the agent has not chosen to resume. Another agent never
          // reaches this branch because the server scopes the row to the initiator.
          considerRecoverable(
            {
              ...offer,
              conference_sid: row.conference_sid,
            },
            RECOVERABLE_RANK.OUTBOUND_RINGING
          );
        } else {
          dispatch('setIncomingCall', offer);
        }
      } else if (status === 'in-progress') {
        // The backend already scoped in-progress rows to this agent (joined_by),
        // so trust it. conference_sid + call_started_at drive the rejoin.
        //
        // serverConfirmed is the flag the widget reads to AUTO-dial a rejoin with no click
        // and no permission prompt, so it may only be set when a silent dial is SAFE. Every
        // other gate on that path is tab-local (the single-shot ref is per component, the
        // active call is set by this tab's own SDK accept handler, the mic query is
        // origin-scoped), and joined_by stays set for the whole call - so "the server lists
        // this row" alone cannot tell a refreshed tab from a SECOND tab opened mid-call. That
        // second tab would silently dial a duplicate agent leg into the live conference: two
        // open mics on one machine, both legs billed, and the conference manager re-points
        // current_agent_call_sid at the new leg, so the original leg dropping is then ignored.
        // rejoinCall deliberately skips the REST claim that would 409 it.
        //
        // Exactly two signals make the dial safe:
        //   * agent_leg_lost - the server has SEEN this agent's leg leave
        //     (agent_connection_lost_at, stamped by the conference manager), so there is no
        //     live leg to duplicate. Absent on an older backend, which simply falls back to
        //     the seed below rather than auto-dialing on a maybe.
        //   * a per-tab ACTIVE_CALL seed naming the SAME callSid - proof this tab held the
        //     call and is coming back from a refresh, which is what this recovery exists for.
        // The per-tab joined_session nonce is NOT usable as a third signal: it is not
        // persisted, so a refresh mints a fresh one and requiring a match would break that
        // same refresh recovery.
        //
        // Without either signal the row is still OFFERED - just as a manual Reconnect, which
        // takes a deliberate click.
        const agentLegLost = row.agent_leg_lost === true;
        const heldByThisTab =
          !!persistedActiveCallSid && persistedActiveCallSid === callSid;
        considerRecoverable(
          {
            ...offer,
            conference_sid: row.conference_sid,
            call_started_at: row.call_started_at,
            serverConfirmed: agentLegLost || heldByThisTab,
          },
          RECOVERABLE_RANK.IN_PROGRESS
        );
      }
    });

    // Commit the winning Reconnect offer once, AFTER every row has been ranked (and before the
    // reconciliation below, which needs the hydrated recoverableCall in state to judge it).
    if (recoverableOffer) {
      dispatch('setRecoverableCall', recoverableOffer);
    }

    // Reconciliation: drop EACH locally-queued incoming offer the server no
    // longer reports, unless a realtime event re-touched it after this fetch
    // began. Snapshot the sids first so clearing (which mutates the queue) does
    // not disturb the iteration.
    currentState.incomingCalls
      .map(call => call?.callSid)
      .filter(Boolean)
      .forEach(incomingSid => {
        if (presentCallSids.has(incomingSid)) return;
        const mutatedAt = callMutationTimestamps.get(incomingSid);
        const mutatedAfterHydrate =
          mutatedAt != null && mutatedAt > hydrateStartedAt;
        if (!mutatedAfterHydrate) {
          dispatch('clearIncomingCall', incomingSid);
        }
      });

    // Same reconciliation for a seeded Reconnect offer: a recoverableCall the
    // module-init seed restored from the persisted active blob but the
    // server no longer reports means the call ended during the refresh, so the
    // stale Reconnect must go (and its persistence with it). Skip if a realtime
    // event re-touched the callSid after this fetch began.
    const recoverableSid = currentState.recoverableCall?.callSid;
    if (recoverableSid && !presentCallSids.has(recoverableSid)) {
      const mutatedAt = callMutationTimestamps.get(recoverableSid);
      const mutatedAfterHydrate =
        mutatedAt != null && mutatedAt > hydrateStartedAt;
      if (!mutatedAfterHydrate) {
        dispatch('clearRecoverableCall');
        clearPersistedActiveCall();
      }
    }
  },

  setCallError({ commit }, error) {
    commit('SET_CALL_ERROR', error);
  },

  clearCallError({ commit }) {
    commit('CLEAR_CALL_ERROR');
    commit('CLEAR_RETRYABLE_OUTBOUND');
  },

  // Retract a surfaced error ONLY when it matches `code`, leaving any retryable-outbound context
  // intact (unlike clearCallError, which also clears it). Used to clear a transient
  // NO_AUDIO_OUTPUT banner when audio recovers mid-call, without wiping an unrelated error.
  clearCallErrorByCode({ commit, state: currentState }, code) {
    if (currentState.callError?.code === code) {
      commit('CLEAR_CALL_ERROR');
    }
  },

  // Raise a transient mid-call media warning. App.vue drains the queue into self-dismissing
  // toasts and empties it, so this is a one-shot channel rather than durable state - which is
  // why a warning never needs (and never gets) a Dismiss control of its own.
  setCallWarning({ commit }, code) {
    if (!code) return;
    commit('ADD_CALL_WARNING', code);
  },

  clearCallWarnings({ commit }) {
    commit('CLEAR_CALL_WARNINGS');
  },
};

const mutations = {
  // consult nests INSIDE activeCall, so the three call-state resets (SET_ACTIVE_CALL,
  // CLEAR_ACTIVE_CALL, RESET_CALL_STATE) drop it for free - a stale per-leg consult surviving into
  // a new call (the #465 class of bug) is unrepresentable. A null consult clears the panel.
  SET_ACTIVE_CALL_CONSULT($state, consult) {
    if (!$state.activeCall) return;
    $state.activeCall = { ...$state.activeCall, consult };
  },

  SET_ACTIVE_CALL($state, callData) {
    stampCallMutation(callData?.callSid);
    $state.activeCall = callData;
    // A call becoming active supersedes any prior media/join error.
    $state.callError = null;
  },
  CLEAR_ACTIVE_CALL($state) {
    stampCallMutation($state.activeCall?.callSid);
    // callError is intentionally preserved here: a call can end *because* of
    // an error (failed join, dead audio), and Twilio fires disconnect after
    // error, so clearing it would hide the failure from the agent.
    $state.activeCall = null;
  },
  ADD_INCOMING_CALL($state, callData) {
    stampCallMutation(callData?.callSid);
    const idx = $state.incomingCalls.findIndex(
      call => call.callSid === callData?.callSid
    );
    if (idx === -1) {
      // Append: FIFO order is arrival order, so the primary (getIncomingCall) is
      // the oldest pending offer.
      $state.incomingCalls.push(callData);
    } else {
      // Same-sid re-offer: update in place (dedup), preserving queue position.
      $state.incomingCalls.splice(idx, 1, callData);
    }
  },
  REMOVE_INCOMING_CALL($state, callSid) {
    stampCallMutation(callSid);
    $state.incomingCalls = $state.incomingCalls.filter(
      call => call.callSid !== callSid
    );
  },
  SET_OUTBOUND_CALL($state, callData) {
    stampCallMutation(callData?.callSid);
    $state.outboundCall = callData;
  },
  CLEAR_OUTBOUND_CALL($state) {
    stampCallMutation($state.outboundCall?.callSid);
    $state.outboundCall = null;
  },
  SET_RECOVERABLE_CALL($state, callData) {
    stampCallMutation(callData?.callSid);
    $state.recoverableCall = callData;
  },
  CLEAR_RECOVERABLE_CALL($state) {
    stampCallMutation($state.recoverableCall?.callSid);
    $state.recoverableCall = null;
  },
  SET_CALL_ERROR($state, error) {
    $state.callError = error;
  },
  CLEAR_CALL_ERROR($state) {
    $state.callError = null;
  },
  SET_RETRYABLE_OUTBOUND($state, context) {
    $state.retryableOutbound = context;
  },
  CLEAR_RETRYABLE_OUTBOUND($state) {
    $state.retryableOutbound = null;
  },
  ADD_CALL_WARNING($state, code) {
    // REPLACE the array rather than pushing: an in-place push keeps the same reference, and a
    // non-deep watcher on the getter would never see it.
    $state.callWarnings = [...$state.callWarnings, code];
  },
  CLEAR_CALL_WARNINGS($state) {
    $state.callWarnings = [];
  },
  // One atomic wipe of every call-state slot (see resetVoiceSession). Every live callSid is
  // stamped as mutated first, so a hydrate that started before the switch and resolves after
  // it cannot re-seed an offer we just cleared.
  RESET_CALL_STATE($state) {
    stampCallMutation($state.activeCall?.callSid);
    $state.incomingCalls.forEach(call => stampCallMutation(call?.callSid));
    stampCallMutation($state.outboundCall?.callSid);
    stampCallMutation($state.recoverableCall?.callSid);
    $state.activeCall = null;
    $state.incomingCalls = [];
    $state.outboundCall = null;
    $state.recoverableCall = null;
    $state.callError = null;
    $state.retryableOutbound = null;
    $state.callWarnings = [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  // Test-only hook to reset the module-level race-guard tracking between cases.
  resetCallTrackingForTest: resetCallMutationTracking,
};
